import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, CheckCircle2, PlayCircle, FileText, BarChart3 } from "lucide-react";
import { PageHeader, Panel, Badge } from "@/components/admin/ui";
import { LessonViewer } from "./LessonViewer";
import { LessonEditor } from "./LessonEditor";
import TrainingProgress from "./TrainingProgress";

export default function Training() {
  const { sid } = useParams();
  const { user } = useAuth();
  const canManage = ["super_admin", "admin"].includes(user?.role) || user?.permissions?.training === "manage";
  const canSeeProgress = canManage || user?.role === "manager";
  const [lessons, setLessons] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("library");

  const load = useCallback(() => {
    api.get(`/training/lessons${sid ? `?store_id=${sid}` : ""}`).then((r) => setLessons(r.data)).catch(() => setLessons([]));
  }, [sid]);
  useEffect(() => { load(); api.get("/training/categories").then((r) => setCats(r.data)); }, [load]);

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"?`)) return;
    try { await api.delete(`/training/lessons/${l.id}`); toast.success("Lesson deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const shown = lessons.filter((l) => !cat || l.category === cat);
  const grouped = cats.map((c) => [c, shown.filter((l) => l.category === c)]).filter(([, ls]) => ls.length);
  const done = lessons.filter((l) => l.my_status === "completed").length;

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Team" title="Training & SOP" testId="training-title"
        subtitle={`${lessons.length} lessons · you've completed ${done}`}
        actions={<>
          {canSeeProgress && (
            <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1">
              <button onClick={() => setTab("library")} className={`px-3 h-8 rounded-full text-sm ${tab === "library" ? "bg-neutral-900 text-white" : ""}`} data-testid="training-tab-library"><GraduationCap className="w-4 h-4 inline mr-1"/>Library</button>
              <button onClick={() => setTab("progress")} className={`px-3 h-8 rounded-full text-sm ${tab === "progress" ? "bg-neutral-900 text-white" : ""}`} data-testid="training-tab-progress"><BarChart3 className="w-4 h-4 inline mr-1"/>Employee Progress</button>
            </div>
          )}
          {canManage && <Button onClick={() => setEditing({})} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="training-new"><Plus className="w-4 h-4 mr-1"/>New Lesson</Button>}
        </>}
      />

      {tab === "progress" ? <TrainingProgress storeId={sid}/> : (
        <>
          <div className="flex gap-2 flex-wrap mb-6">
            <button onClick={() => setCat("")} className={`px-3 h-8 rounded-full text-sm border ${!cat ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200"}`} data-testid="training-cat-all">All</button>
            {cats.map((c) => <button key={c} onClick={() => setCat(c)} className={`px-3 h-8 rounded-full text-sm border ${cat === c ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200"}`} data-testid={`training-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>)}
          </div>
          {grouped.length === 0 && <Panel className="p-10 text-center text-sm text-neutral-500" data-testid="training-empty">No lessons yet.</Panel>}
          {grouped.map(([c, ls]) => (
            <div key={c} className="mb-8">
              <h2 className="font-display font-semibold text-lg mb-3">{c}</h2>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {ls.map((l) => (
                  <Panel key={l.id} className="p-4 flex flex-col hover:shadow-sm transition-shadow" data-testid={`lesson-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <button onClick={() => setViewing(l.id)} className="text-left font-semibold hover:text-[hsl(var(--primary))]" data-testid={`lesson-open-${l.id}`}>{l.title}</button>
                      {l.my_status === "completed" ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0"/> : l.my_status === "in_progress" ? <PlayCircle className="w-5 h-5 text-blue-500 shrink-0"/> : null}
                    </div>
                    <p className="text-sm text-neutral-500 flex-1 line-clamp-2">{l.description}</p>
                    <div className="flex items-center justify-between mt-3 text-xs text-neutral-500">
                      <span>{l.step_count} steps{l.pdf_url ? <> · <FileText className="w-3 h-3 inline"/> SOP</> : ""}{!l.published ? <> · <Badge tone="orange">draft</Badge></> : ""}</span>
                      <span className="flex items-center gap-1">
                        <Badge>{l.my_status}</Badge>
                        {canManage && <>
                          <button onClick={() => setEditing(l)} className="p-1 text-neutral-400 hover:text-neutral-900" data-testid={`lesson-edit-${l.id}`}><Pencil className="w-3.5 h-3.5"/></button>
                          <button onClick={() => remove(l)} className="p-1 text-neutral-400 hover:text-red-600" data-testid={`lesson-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                        </>}
                      </span>
                    </div>
                  </Panel>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {viewing && <LessonViewer id={viewing} onClose={() => { setViewing(null); load(); }}/>}
      {editing && <LessonEditor lesson={editing.id ? editing : null} categories={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}
